import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, Subscription, catchError, of, switchMap, throwError, timer } from 'rxjs';

/**
 * The debug wrapper's test-account roster, live session indicator and info card
 * (notes/debug-wrapper-v2.md). Read-only: every call is a GET to `/bff/debug/accounts`, which the
 * BFF serves only on lower-environment debug hosts.
 *
 * Demo-agnostic by construction: a session's acting identity arrives as an opaque, server-made
 * `actingLabel` string. Nothing here imports or knows about demo personas, and nothing here is a
 * sign-in choice (the picker stays the PersonaService roster of actual users).
 */

/** How often the roster (and so every badge) refreshes while the wrapper is open. */
export const TEST_ACCOUNT_POLL_MS = 10_000;

export type SessionSurface = 'customer-portal' | 'backoffice' | 'applicant';
export type SessionHost = 'debug-host' | 'ordinary-host';
export type SessionEntry = 'debug-login' | 'debug-handoff' | 'interactive-login';
export type SessionActing = 'self' | 'demo-persona' | 'townsim-walk-in' | 'view-as' | 'debug-swap' | 'unknown';

export interface SessionGroup {
  surface: SessionSurface | string;
  host: SessionHost | string;
  entry: SessionEntry | string;
  acting: SessionActing | string;
  actingLabel: string | null;
  demoInstance: boolean;
  count: number;
  lastActiveAt: string;
}

/** Counts per session kind. Never a session id, token or IP. */
export interface SessionActivity {
  status: 'ok' | 'unavailable';
  total: number;
  truncated: boolean;
  observedAt: string;
  groups: SessionGroup[];
  notObserved: string[];
}

export type TestAccountLifecycle = 'active' | 'ready' | 'not-ready' | 'proposed' | 'uncatalogued'
  | 'provisioning' | 'needs-reprovision' | 'blocked' | 'retiring' | 'retired';
export interface CuratedTestAccountMetadata {
  createdBy?: { kind: 'login' | 'import' | 'unknown' | string; loginId: string | null } | null;
  createdByTestAccount?: boolean;
  contact?: string | null;
  blockedReason?: { code: string; text: string } | null;
  linkedPartnerApps?: string[];
}

/** Optional provenance, displayed only when the server records a usable creator. */
export function creatorText(metadata: CuratedTestAccountMetadata | null | undefined): string | null {
  const creator = metadata?.createdBy;
  const loginId = creator?.loginId?.trim();
  if (!creator || creator.kind === 'unknown' || !loginId) return null;
  return `by ${loginId}${metadata?.createdByTestAccount ? ' (test account)' : ''}`;
}

/** Join only available facts so a missing creator never leaves a leading separator. */
export function curatorLine(
  metadata: CuratedTestAccountMetadata | null | undefined,
  { contactFallback }: { contactFallback?: string } = {},
): string {
  const parts: string[] = [];
  const creator = creatorText(metadata);
  const contact = metadata?.contact?.trim() || contactFallback?.trim();
  if (creator) parts.push(creator);
  if (contact) parts.push(`contact ${contact}`);
  return parts.join(' · ');
}

export interface TestAccountSummary extends CuratedTestAccountMetadata {
  username: string;
  displayName: string;
  audience: 'customer' | 'staff' | 'unknown';
  lifecycle: TestAccountLifecycle;
  signInAvailable: boolean;
  sessions: SessionActivity;
}

export interface LiveRealm {
  status: string;
  detail: string | null;
  exists: boolean | null;
  enabled: boolean | null;
  realmRoles: string[];
  credentialTypes: string[];
}

export interface LiveOrgs {
  status: string;
  detail: string | null;
  memberships: { orgId: string; role: string | null }[];
}

export interface LiveActionGrant {
  action: string;
  orgWide: boolean;
  accountCount: number;
  velocityRemaining: number | null;
}

export interface LiveGrants {
  status: string;
  detail: string | null;
  byOrg: { orgId: string; status: string; actions: LiveActionGrant[]; truncated: boolean }[];
}

export interface LivePersonas {
  status: string;
  note: string | null;
  keys: string[];
}

export interface TestAccountInfo extends CuratedTestAccountMetadata {
  username: string;
  subject: string | null;
  partnerLinksStatus?: string;
  displayName: string;
  audience: 'customer' | 'staff';
  lifecycle: TestAccountLifecycle;
  signInAvailable: boolean;
  owner: string | null;
  purpose: string;
  scenarios: string[];
  avoidFor: string[];
  knownIssues: string[];
  coverageGap: string | null;
  followUps: string[];
  proposedRealm: { realmRoles: string[] } | null;
  live: { realm: LiveRealm; orgs: LiveOrgs; grants: LiveGrants; personas: LivePersonas };
  sessions: SessionActivity;
}

const SURFACE_LABEL: Record<string, string> = {
  'customer-portal': 'customer portal',
  backoffice: 'back office',
  applicant: 'applicant',
};

const ENTRY_LABEL: Record<string, string> = {
  'debug-login': 'debug host',
  'debug-handoff': 'debug handoff to normal host',
  'interactive-login': 'normal sign-in',
};

/** One human line for a session kind, e.g. "customer portal, debug host, as Persephone_Payroll (demo)". */
export function describeSessionGroup(group: SessionGroup): string {
  const parts = [SURFACE_LABEL[group.surface] ?? group.surface, ENTRY_LABEL[group.entry] ?? group.entry];
  switch (group.acting) {
    case 'demo-persona':
      parts.push(group.actingLabel ? `as ${group.actingLabel}` : 'as a demo persona');
      break;
    case 'townsim-walk-in':
      parts.push('TownSim walk-in');
      break;
    case 'view-as':
      parts.push('view-as');
      break;
    case 'debug-swap':
      parts.push('debug swap');
      break;
    case 'unknown':
      parts.push('acting context unreadable');
      break;
    default:
      break;
  }
  const line = parts.join(', ');
  return group.demoInstance ? `${line} (demo instance)` : line;
}

/** Accessible one-sentence summary of an account's live sessions. */
export function describeActivity(activity: SessionActivity | undefined): string {
  if (!activity) return 'Live sessions not loaded yet';
  if (activity.status !== 'ok') return 'Live session count unavailable';
  if (activity.total === 0) return 'No live sessions';
  const kinds = activity.groups.map((g) => `${g.count} ${describeSessionGroup(g)}`).join('; ');
  const noun = activity.total === 1 ? 'live session' : 'live sessions';
  return `${activity.total}${activity.truncated ? '+' : ''} ${noun}: ${kinds}`;
}

@Injectable({ providedIn: 'root' })
export class TestAccountsService {
  // Optional so hand-built shell specs that mock every other service keep working; without an
  // HttpClient the indicator simply never loads.
  private readonly http = inject(HttpClient, { optional: true });
  private poll: Subscription | null = null;

  readonly roster = signal<TestAccountSummary[]>([]);
  readonly loadState = signal<'idle' | 'loading' | 'ok' | 'error'>('idle');

  /** username -> live session activity, for the per-account badges. */
  readonly activityByUsername = computed(() => {
    const map = new Map<string, SessionActivity>();
    if (this.loadState() !== 'ok') return map;
    for (const account of this.roster()) map.set(account.username, account.sessions);
    return map;
  });

  /** Every visible non-active or denied entry, including informational stale rows on read failure. */
  readonly notYetSignInable = computed(() =>
    this.roster().filter((account) => this.loadState() !== 'ok' || !account.signInAvailable),
  );

  /** Start refreshing the roster every {@link TEST_ACCOUNT_POLL_MS}. Idempotent. */
  start(): void {
    if (!this.http || this.poll) return;
    this.poll = timer(0, TEST_ACCOUNT_POLL_MS)
      .pipe(switchMap(() => (this.pageHidden() ? of(null) : this.fetchRoster())))
      .subscribe((roster) => {
        if (roster === null) return;
        if (Array.isArray(roster)) {
          this.roster.set(roster);
          this.loadState.set('ok');
        } else {
          this.loadState.set('error');
        }
      });
  }

  stop(): void {
    this.poll?.unsubscribe();
    this.poll = null;
  }

  /** The info card for one catalogued account. */
  info(username: string): Observable<TestAccountInfo> {
    if (!this.http) return throwError(() => new Error('no HttpClient'));
    return this.http.get<TestAccountInfo>(`/bff/debug/accounts/${encodeURIComponent(username)}`);
  }

  private fetchRoster(): Observable<TestAccountSummary[] | 'error'> {
    if (this.roster().length === 0) this.loadState.set('loading');
    return this.http!.get<TestAccountSummary[]>('/bff/debug/accounts').pipe(
      catchError(() => of('error' as const)),
    );
  }

  private pageHidden(): boolean {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
  }
}
